import {
  StructuredTableColumn,
  StructuredTableColumnSummary,
  TagValueStructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {CreateProcessButtonQuery} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {requiredLinkToSchemaId, getRequiredLinkFolderItemType} from 'schemas/fields/SchemaFieldConstants';

type ProcessTaskSchema = CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

function getDestinationColumnSummary(
  column: StructuredTableColumn,
  schema: ProcessTaskSchema
): {
  destinationSchemaIdConstraint: string | null;
  destinationFolderItemType: string | null;
  destinationSchemaFieldIsSnapshot: boolean;
} {
  const matchingField = schema.fields.find(
    (field: ClientSchemaField) => column.schemaFieldApiId === field.apiId
  );

  let requiredLinkSchemaId: string | null = null;
  let requiredLinkFolderItemType: string | null = null;
  if (matchingField?.requiredLink != null) {
    requiredLinkSchemaId = requiredLinkToSchemaId(matchingField.requiredLink);
    requiredLinkFolderItemType = getRequiredLinkFolderItemType(matchingField.requiredLink);
  }

  return {
    destinationSchemaIdConstraint: requiredLinkSchemaId,
    destinationFolderItemType: requiredLinkFolderItemType,
    destinationSchemaFieldIsSnapshot: matchingField?.isSnapshot ?? false,
  };
}

export function findMatchingDestinationColumnIndices(
  columnSummary: StructuredTableColumnSummary,
  destinationSchema: ProcessTaskSchema,
  destinationTable: UnknownStructuredTable
): Array<number> {
  const matchingColIndices: Array<number> = [];
  for (const [colIndex, destinationColumnCandidate] of destinationTable.columns.entries()) {
    const {destinationSchemaIdConstraint, destinationFolderItemType, destinationSchemaFieldIsSnapshot} =
      getDestinationColumnSummary(destinationColumnCandidate, destinationSchema);
    // Check if the destination column satisfies any of the source column requirements
    for (const destinationColumnRequirement of columnSummary.validDestinationColumnRequirements) {
      if (destinationSchemaFieldIsSnapshot) {
        continue;
      } else if (destinationColumnRequirement.columnType !== destinationColumnCandidate.columnType) {
        continue;
      } else if (
        destinationColumnRequirement?.schemaFieldType != null &&
        destinationColumnRequirement.schemaFieldType !==
          (destinationColumnCandidate as TagValueStructuredTableColumn).schemaFieldType
      ) {
        continue;
      } else if (
        destinationFolderItemType != null &&
        destinationColumnRequirement.folderItemType !== destinationFolderItemType
      ) {
        continue;
      } else if (
        destinationSchemaIdConstraint != null &&
        columnSummary.schemaIdConstraint !== destinationSchemaIdConstraint
      ) {
        continue;
      }

      matchingColIndices.push(colIndex);
    }
  }
  return matchingColIndices;
}

export default function findMatchingDestinationColumnIndex(
  columnSummary: StructuredTableColumnSummary,
  destinationSchema: ProcessTaskSchema,
  destinationTable: UnknownStructuredTable
): number | null {
  let matchingColIndex = null;
  for (const [colIndex, destinationColumnCandidate] of destinationTable.columns.entries()) {
    const {destinationSchemaIdConstraint, destinationFolderItemType, destinationSchemaFieldIsSnapshot} =
      getDestinationColumnSummary(destinationColumnCandidate, destinationSchema);
    // Check if the destination column satisfies any of the source column requirements
    for (const destinationColumnRequirement of columnSummary.validDestinationColumnRequirements) {
      if (destinationSchemaFieldIsSnapshot) {
        continue;
      } else if (destinationColumnRequirement.columnType !== destinationColumnCandidate.columnType) {
        continue;
      } else if (
        destinationColumnRequirement?.schemaFieldType != null &&
        destinationColumnRequirement.schemaFieldType !==
          (destinationColumnCandidate as TagValueStructuredTableColumn).schemaFieldType
      ) {
        continue;
      } else if (
        destinationFolderItemType != null &&
        destinationColumnRequirement.folderItemType !== destinationFolderItemType
      ) {
        continue;
      } else if (
        destinationSchemaIdConstraint != null &&
        columnSummary.schemaIdConstraint !== destinationSchemaIdConstraint
      ) {
        continue;
      }

      if (
        destinationSchemaIdConstraint != null &&
        columnSummary.schemaIdConstraint === destinationSchemaIdConstraint
      ) {
        return colIndex;
      } else if (matchingColIndex == null) {
        matchingColIndex = colIndex;
      }
    }
  }
  return matchingColIndex;
}
